import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, OnDestroy, signal, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CoreActions } from '@next/core-lib';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, skip, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  FertilizationBalanceSetting,
  FertilizationBalanceSettingsResponse,
  SamplesBy,
  SiteState,
  SpecificReferenceBlock,
} from 'src/app/shared/models';

import { ApiService } from 'src/app/shared/services/api.service';
import { TranslationHelpers } from 'src/app/shared/util/translationHelpers';

import { EditFertilizationBalanceSettingsDialogHelper } from './edit-fertilization-balance-settings-dialog.helper';
import { FertilizationBalanceEditModel } from './edit-fertilization-balance-settings-dialog.model';

import {
  FertilizationBalanceSettingsCancellationConfirmDialogComponent,
} from './fertilization-balance-settings-cancellation-confirm-dialog/fertilization-balance-settings-cancellation-confirm-dialog.component';
import {
  FertilizationBalanceSettingsParticipationConfirmDialogComponent,
} from './fertilization-balance-settings-participation-confirm-dialog/fertilization-balance-settings-participation-confirm-dialog.component';
import {
  SpecificReferenceBlockDialogComponent,
  SpecificReferenceBlockDialogData,
  SpecificReferenceBlockDialogResult,
}
  from
  './specific-reference-block-dialog/specific-reference-block-dialog.component';
import { UpdateExpectedCropYield } from '../../store/actions';

@Component({
  selector: 'app-edit-fertilization-balance-settings-dialog',
  templateUrl: './edit-fertilization-balance-settings-dialog.component.html',
  styleUrls: ['./edit-fertilization-balance-settings-dialog.component.scss'],
  standalone: false,
})
export class EditFertilizationBalanceSettingsDialogComponent
  extends AbstractFormDialog<{ fertilizationBalanceSettings: FertilizationBalanceSetting[] }> implements OnDestroy {
  data = inject<FertilizationBalanceEditModel>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly translationHelpers = inject(TranslationHelpers);
  private readonly locale = inject(LocaleService);
  protected mdDialogRef = inject<MatDialogRef<EditFertilizationBalanceSettingsDialogComponent, AbstractDialogResultData<never>>>(MatDialogRef);


  private readonly fertilizationBalanceSettings = signal<FertilizationBalanceSettingsResponse>({
  fertilizationBalanceSettings: [],
  headerBusinessRuleViolations: [],
});
  readonly cropYear = signal<number>(0);
  private readonly destroyed$ = new Subject<void>();

  formGroupByAdviceId: { [adviceId: number]: FormGroup } = {};
  formArray: FormArray<FormGroup<unknown>> = this.formBuilder.array([]) as unknown as FormArray<FormGroup<unknown>>;
  formGroup = this.formBuilder.group({
    fertilizationSettingsWithName: this.formArray,
  });
  samplesByKeys = Object.keys(SamplesBy);

  readonly sendParticipationMailDisabled = computed<boolean>(() =>
    this.participatingSettingsWithoutMailDate().length === 0,
  );

  readonly sendCancellationMailDisabled = computed<boolean>(() =>
    this.cancellationSettingsWithoutConfirmationDate().length === 0,
  );

  readonly headerWarningTranslationCodes =
  (this.data.headerBusinessRuleViolations).map(
    (translationCode) => `fertilizationBalance.validation.${translationCode}`,
  );

  readonly sortedSettings = computed<FertilizationBalanceSetting[]>(() =>
    [...this.fertilizationBalanceSettings().fertilizationBalanceSettings].sort((a, b) => a.name.localeCompare(b.name)),
  );

  private readonly participatingSettingsWithoutMailDate = signal<FertilizationBalanceSetting[]>([]);
  private readonly cancellationSettingsWithoutConfirmationDate = signal<FertilizationBalanceSetting[]>([]);

  constructor() {
    super();
    const data = this.data;

    this.fertilizationBalanceSettings.set({
      fertilizationBalanceSettings: this.data.fertilizationBalanceSettings || [],
      headerBusinessRuleViolations: this.data.headerBusinessRuleViolations || [],
    });
    this.cropYear.set(data.cropYear || 0);
    this.setupFormGroups();
  }

  getFormControl(adviceId: number, controlName: string): FormControl {
    return this.formGroupByAdviceId[adviceId]?.get(controlName) as FormControl;
  }

  getWarningLabelTranslation(translationCode: string): string {
    return this.locale.translate('fertilizationBalance.validation.' + translationCode);
  }

  openEditSpecificReferenceBlockDialog(fertilizationBalanceSetting: FertilizationBalanceSetting): void {
    this.openSpecificReferenceBlockDialog(fertilizationBalanceSetting, false)
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  private setupReactiveHandlers(setting: FertilizationBalanceSetting) {
    const formGroup = this.formGroupByAdviceId[setting.adviceId];

    // Handle participation changes
    formGroup.get('isParticipating')?.valueChanges.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      switchMap((isParticipating) =>
        isParticipating
          ? this.updateParticipation$(setting.adviceId, formGroup.get('samplesBy')?.value)
          : this.api.patchNotParticipating(setting.adviceId).pipe(
            tap(() => this.updateParticipationStatus(setting, false)),
            catchError(() => EMPTY),
          ),
      ),
    ).subscribe();

    // Handle cancellation changes
    formGroup.get('isCanceling')?.valueChanges.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      switchMap((isCanceling) =>
        isCanceling
          ? this.updateIsCanceling(setting.adviceId, isCanceling)
          : this.api.patchCanceling(setting.adviceId, false).pipe(
            tap(() => this.updateCancellationStatus(setting, false)),
            catchError(() => EMPTY),
          ),
      ),
    ).subscribe();

    // Handle expectedCropYield changes
    formGroup.get('expectedCropYieldProduct')?.valueChanges.pipe(
      startWith(formGroup.get('expectedCropYieldProduct')?.value),
      distinctUntilChanged(),
      skip(1),
      filter((expectedCropYieldProduct): expectedCropYieldProduct is number =>
        typeof expectedCropYieldProduct === 'number' && Number.isFinite(expectedCropYieldProduct),
      ),
      debounceTime(500),
      takeUntil(this.destroyed$),
      tap((expectedCropYieldProduct) => this.store.dispatch(new UpdateExpectedCropYield(
        setting.adviceId,
        this.cropYear(),
        expectedCropYieldProduct,
      ))),
    ).subscribe();

    // Handle specificReferenceBlock changes
    formGroup.get('isSpecificReferenceBlock')?.valueChanges.pipe(
      takeUntil(this.destroyed$),
      filter(() => !EditFertilizationBalanceSettingsDialogHelper.isSpecificReferenceBlockLocked(
        this.findSettingByAdviceId(setting.adviceId),
      )),
      switchMap((isSpecificReferenceBlock) =>
        isSpecificReferenceBlock
          ? this.setSpecificReferenceBlock(setting.adviceId)
          : this.removeSpecificReferenceBlock(setting.adviceId, isSpecificReferenceBlock),
      ),
    ).subscribe();

    // Disable fields based on business rules
    this.disableFieldsBasedOnRules(setting, formGroup);
  }

  private disableFieldsBasedOnRules(setting: FertilizationBalanceSetting, formGroup: FormGroup) {
    formGroup.get('participationMailDate')?.disable();
    formGroup.get('externalOrderId')?.disable();
    formGroup.get('orderDate')?.disable();

    const isParticipationBlocked = this.isParticipationBlocked() || setting.businessRuleViolationTranslationCodes.length > 0;
    if (isParticipationBlocked || !!setting.participationMailDate) {
      formGroup.get('isParticipating')?.disable({ emitEvent: false });
    }

    const isCancellationDisabled = !setting.participationMailDate || !!setting.cancelationDate
    || !!setting.customerPlantDate || !!setting.areSampleDatesConfirmed;
    if (isCancellationDisabled) {
      formGroup.get('isCanceling')?.disable({ emitEvent: false });
    }

    if (EditFertilizationBalanceSettingsDialogHelper.isSpecificReferenceBlockLocked(setting)) {
      formGroup.get('isSpecificReferenceBlock')?.disable({ emitEvent: false });
    }
  }

  private updateParticipation$(adviceId: number, samplesBy: SamplesBy) {
    return this.api.patchParticipation(adviceId, samplesBy).pipe(
      tap(() => this.updateParticipationStatus(this.findSettingByAdviceId(adviceId), true, samplesBy)),
      catchError((error: HttpErrorResponse) => {
        this.formGroupByAdviceId[adviceId]?.get('isParticipating')?.setValue(false);
        this.handleValidationError(error);
        return EMPTY;
      }),
    );
  }

  private updateIsCanceling(adviceId: number, isCanceling: boolean) {
    return this.api.patchCanceling(adviceId, isCanceling).pipe(
      tap(() => this.updateCancellationStatus(this.findSettingByAdviceId(adviceId), isCanceling)),
      catchError((error: HttpErrorResponse) => {
        this.formGroupByAdviceId[adviceId]?.get('isCanceling')?.setValue(false);
        this.handleValidationError(error);
        return EMPTY;
      }),
    );
  }

  private updateParticipationStatus(setting: FertilizationBalanceSetting, isParticipating: boolean, samplesBy?: SamplesBy) {
    const updated: FertilizationBalanceSetting = {
      ...setting,
      isParticipating,
      ...(samplesBy ? { samplesBy } : {}),
    };
    this.onParticipationChange(updated);
  }

  private updateCancellationStatus(setting: FertilizationBalanceSetting, isCanceling: boolean) {
    const updated: FertilizationBalanceSetting = {
      ...setting,
      isCanceling,
    };
    this.onCancellationChange(updated);
  }

  private findSettingByAdviceId(adviceId: number): FertilizationBalanceSetting {
    const setting = this.fertilizationBalanceSettings().fertilizationBalanceSettings.find((s) => s.adviceId === adviceId);
    if (!setting) {
      throw new Error(`Setting with adviceId ${adviceId} not found`);
    }
    return setting;
  }

  private handleValidationError(error: HttpErrorResponse) {
    if (error.status === 400 || error.status === 500) {
      const message = this.locale.translate('fertilizationBalance.validation.GenericErrorUpdatingExpectedCropYield');
      this.store.dispatch(NotificationActions.infoSnackbar({ message }));
    }
  }

  private openSpecificReferenceBlockDialog(
    fertilizationBalanceSetting: FertilizationBalanceSetting,
    revertOnCancel = false,
  ): Observable<void> {
    if (EditFertilizationBalanceSettingsDialogHelper.isSpecificReferenceBlockLocked(fertilizationBalanceSetting)) {
      return EMPTY;
    }

    const dialogRef = this.dialog.open<SpecificReferenceBlockDialogComponent, SpecificReferenceBlockDialogData, SpecificReferenceBlockDialogResult>(
      SpecificReferenceBlockDialogComponent,
      {
        id: 'specific-reference-block-dialog',
        width: '560px',
        height: '400px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100dvh - 32px)',
        data: { fertilizationBalanceSetting, mode: revertOnCancel ? 'create' : 'edit' },
      },
    );

    return dialogRef.afterClosed().pipe(
      switchMap((result) => {
        const formGroup = this.formGroupByAdviceId[fertilizationBalanceSetting.adviceId];

        if (!result || result.action === 'cancel') {
          if (revertOnCancel) {
            formGroup.get('isSpecificReferenceBlock')?.setValue(false, { emitEvent: false });
          }
          return EMPTY;
        }

        if (result.action === 'remove') {
          return this.removeSpecificReferenceBlock(fertilizationBalanceSetting.adviceId, false);
        }

        return this.api.patchSpecificReferenceBlock(fertilizationBalanceSetting.adviceId, result.data).pipe(
          tap(() => {
            this.updateFertilizationBalanceSetting(fertilizationBalanceSetting.adviceId, result.data);
            this.store.dispatch(NotificationActions.successSnackbar({
              message: this.locale.translate('fertilizationBalance.setSpecificReferenceBlockSuccess'),
            }));
          }),
          catchError((error) => {
            if (revertOnCancel) {
              formGroup.get('isSpecificReferenceBlock')?.setValue(false, { emitEvent: false });
            }
            const validationText = this.translationHelpers.generateLocalisedValidationText(
              error.error,
              'fertilizationBalance.validation',
            );
            this.store.dispatch(NotificationActions.errorSnackbar({ message: validationText }));
            return EMPTY;
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(this.store.dispatch(CoreActions.setLoading({ loading: true }))),
        );
      }),
    );
  }

  private setSpecificReferenceBlock(adviceId: number): Observable<void> {
    const currentSetting = this.findSettingByAdviceId(adviceId);
    const settingWithClearedData: FertilizationBalanceSetting = {
      ...currentSetting,
      specificReferenceBlockData: null,
    };
    return this.openSpecificReferenceBlockDialog(settingWithClearedData, true);
  }

  private removeSpecificReferenceBlock(adviceId: number, toggleValue: boolean): Observable<void> {
    const currentSetting = this.findSettingByAdviceId(adviceId);
    if (EditFertilizationBalanceSettingsDialogHelper.isSpecificReferenceBlockLocked(currentSetting)
      || !EditFertilizationBalanceSettingsDialogHelper.hasSpecificReferenceBlockData(currentSetting.specificReferenceBlockData)) {
      return EMPTY;
    }
    return this.api.removeSpecificReferenceBlock(adviceId).pipe(
            tap(() => {
              const updatedSettings = this.fertilizationBalanceSettings().fertilizationBalanceSettings.map((setting) =>
                setting.adviceId === adviceId ? { ...setting, specificReferenceBlockData: null } : setting,
              );
              this.fertilizationBalanceSettings.set({
                ...this.fertilizationBalanceSettings(),
                fertilizationBalanceSettings: updatedSettings,
              });
              this.formGroupByAdviceId[adviceId].get('isSpecificReferenceBlock')?.setValue(false, { emitEvent: false });
            }),
            catchError((error: HttpErrorResponse) => {
              const validationText = this.translationHelpers.generateLocalisedValidationText(
              error.error,
              'fertilizationBalance.validation',
              );
              this.store.dispatch(NotificationActions.errorSnackbar({ message: validationText }));
              this.formGroupByAdviceId[adviceId].get('isSpecificReferenceBlock')?.setValue(!toggleValue, { emitEvent: false });
              return EMPTY;
            }),
      finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
      startWith(this.store.dispatch(CoreActions.setLoading({ loading: true }))),
    );
  }

  private updateFertilizationBalanceSetting(adviceId: number, specificReferenceBlockData: SpecificReferenceBlock): void {
    const updatedSettings = this.fertilizationBalanceSettings().fertilizationBalanceSettings.map((setting) =>
      setting.adviceId === adviceId ? { ...setting, specificReferenceBlockData } : setting,
    );
    this.fertilizationBalanceSettings.set({
      ...this.fertilizationBalanceSettings(),
      fertilizationBalanceSettings: updatedSettings,
    });
  }

  onParticipationChange(event: FertilizationBalanceSetting) {
    if (this.isEligibleForParticipationMail(event)) {
      const filtered = this.participatingSettingsWithoutMailDate().filter((setting) => setting.id !== event.id);
      this.participatingSettingsWithoutMailDate.set([event, ...filtered]);
    } else {
      this.participatingSettingsWithoutMailDate.set(
        this.participatingSettingsWithoutMailDate().filter((setting) => setting.id !== event.id),
      );
    }
  }

  onCancellationChange(event: FertilizationBalanceSetting) {
    if (this.isEligibleForCancellationMail(event)) {
      const filtered = this.cancellationSettingsWithoutConfirmationDate().filter((setting) => setting.id !== event.id);
      this.cancellationSettingsWithoutConfirmationDate.set([event, ...filtered]);
    } else {
      this.cancellationSettingsWithoutConfirmationDate.set(
        this.cancellationSettingsWithoutConfirmationDate().filter((setting) => setting.id !== event.id),
      );
    }
  }

  setupFormGroups() {
    this.formGroupByAdviceId = this.fertilizationBalanceSettings().fertilizationBalanceSettings.reduce(
      (a, f) => ({
        ...a,
        [f.adviceId]: this.createFormGroup(f),
      }),
      {},
    );
    this.formArray = this.formBuilder.array(Object.values(this.formGroupByAdviceId));
    this.formGroup = this.formBuilder.group({
      fertilizationSettingsWithName: this.formArray,
    });

    this.participatingSettingsWithoutMailDate.set(
      this.fertilizationBalanceSettings().fertilizationBalanceSettings.filter((s) => this.isEligibleForParticipationMail(s)),
    );

    this.cancellationSettingsWithoutConfirmationDate.set(
      this.fertilizationBalanceSettings().fertilizationBalanceSettings.filter((s) => this.isEligibleForCancellationMail(s)),
    );

    // Setup reactive handlers for each form group
    for (const setting of this.fertilizationBalanceSettings().fertilizationBalanceSettings) {
      this.setupReactiveHandlers(setting);
    }
  }

  createFormGroup(f: FertilizationBalanceSetting) {
    return this.formBuilder.group({
      id: f.id,
      adviceId: f.adviceId,
      name: [f.name + ' (' + f.areaInHectares.toFixed(2) + ' Ha)'],
      areaInHectares: [f.areaInHectares],
      cropName: [f.cropName],
      isParticipating: [f.isParticipating],
      isCanceling: [f.isCanceling],
      samplesBy: [{ value: f.samplesBy || SamplesBy.Fertilab, disabled: true }],
      participationMailDate: [f.participationMailDate],
      cancelationDate: [f.cancelationDate],
      externalOrderId: [f.externalOrderId],
      orderDate: [f.orderDate],
      expectedCropYieldProduct: [f.expectedCropYieldProduct],
      isSpecificReferenceBlock: [EditFertilizationBalanceSettingsDialogHelper.hasSpecificReferenceBlockData(f.specificReferenceBlockData)],
    });
  }

  async sendParticipationMail() {
    if (this.participatingSettingsWithoutMailDate().length === 0) {
      this.store.dispatch(NotificationActions.errorSnackbar({
        message: this.locale.translate('fertilizationBalance.validation.NoSettingToSendParticipationMail'),
      }));
      return;
    }

    const dialogRef = this.dialog.open(FertilizationBalanceSettingsParticipationConfirmDialogComponent, {
      id: 'fertilization-balance-settings-participation-confirm-dialog',
      data: { fertilizationBalanceSettings: this.participatingSettingsWithoutMailDate() },
    });

    dialogRef.afterClosed().pipe(
      filter((result) => result === true),
      switchMap(() =>
        this.api.postSendParticipationMail(this.cropYear()).pipe(
          map(() => {
            this.store.dispatch(NotificationActions.successSnackbar({
              message: this.locale.translate('fertilizationBalance.sendParticipationMailSuccess'),
            }));
          }),
          catchError((error) => {
            const validationText = this.translationHelpers.generateLocalisedValidationText(
              error.error,
              'fertilizationBalance.validation',
            );
            this.store.dispatch(NotificationActions.errorSnackbar({ message: validationText }));
            return of();
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(this.store.dispatch(CoreActions.setLoading({ loading: true }))),
        ),
      ),
      switchMap(() =>
        this.api.getFertilizationBalanceSettings(this.cropYear()).pipe(
          map((settings) => {
            this.fertilizationBalanceSettings.set(settings);
            this.setupFormGroups();
          }),
          catchError((error) => {
            const validationText = this.translationHelpers.generateLocalisedValidationText(
              error.error,
              'fertilizationBalance.validation',
            );
            this.store.dispatch(NotificationActions.errorSnackbar({ message: validationText }));
            return of();
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(this.store.dispatch(CoreActions.setLoading({ loading: true }))),
        ),
      ),
    ).subscribe();
  }

  protected async sendCancellationMail() {
    if (this.cancellationSettingsWithoutConfirmationDate().length === 0) {
      this.store.dispatch(NotificationActions.errorSnackbar({
        message: this.locale.translate('fertilizationBalance.validation.NoSettingToSendCancellationMail'),
      }));
      return;
    }

    const dialogRef = this.dialog.open(FertilizationBalanceSettingsCancellationConfirmDialogComponent, {
      id: 'fertilization-balance-settings-cancellation-confirm-dialog',
      data: { fertilizationBalanceSettings: this.cancellationSettingsWithoutConfirmationDate() },
    });

    dialogRef.afterClosed().pipe(
      filter((result) => result === true),
      switchMap(() =>
        this.api.postSendCancellationMail(this.cropYear()).pipe(
          map(() => {
            this.store.dispatch(NotificationActions.successSnackbar({
              message: this.locale.translate('fertilizationBalance.sendCancellationMailSuccess'),
            }));
          }),
          catchError((error) => {
            const validationText = this.translationHelpers.generateLocalisedValidationText(
              error.error,
              'fertilizationBalance.validation',
            );
            this.store.dispatch(NotificationActions.errorSnackbar({ message: validationText }));
            return of();
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(() => this.store.dispatch(CoreActions.setLoading({ loading: true }))),
        ),
      ),
      switchMap(() =>
        this.api.getFertilizationBalanceSettings(this.cropYear()).pipe(
          map((settings) => {
            this.fertilizationBalanceSettings.set(settings);
            this.setupFormGroups();
          }),
          catchError((error) => {
            const validationText = this.translationHelpers.generateLocalisedValidationText(
              error.error,
              'fertilizationBalance.validation',
            );
            this.store.dispatch(NotificationActions.errorSnackbar({ message: validationText }));
            return of();
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(() => this.store.dispatch(CoreActions.setLoading({ loading: true }))),
        ),
      ),
    ).subscribe();
  }

  private isEligibleForParticipationMail(setting: FertilizationBalanceSetting) {
    return setting.isParticipating && !setting.participationMailDate;
  }

  private isEligibleForCancellationMail(setting: FertilizationBalanceSetting) {
    return setting.isCanceling && !setting.cancelationDate;
  }

  isParticipationBlocked(): boolean {
  return this.data.headerBusinessRuleViolations.length > 0;
}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
